'use client';

import React, { memo } from 'react';

import RichText from '@/components/ui/RichText/RichText';
import { COMPONENT_STYLES } from '@/constants/styles';
import { useCountAnimation } from '@/hooks/useCountAnimation';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import {
  StatisticComponent,
  StatisticsMetricsProps,
} from '@/types/statisticsMetrics';

import { extractNumber, formatDisplayValue } from './statisticsHelpers';

/**
 * AnimatedStatisticCard - Renders an individual statistic card with count-up logic.
 */
const AnimatedStatisticCard: React.FC<{
  item: StatisticComponent;
  delay: number;
}> = memo(({ item, delay }) => {
  // Normalize the data source (supports both RichText and raw strings)
  const mainValue = item.description?.json || item.text;
  const label = item.subDescription?.json || item.text2 || item.title;

  const targetNumber = extractNumber(mainValue);

  const { ref: countRef, count } = useCountAnimation({
    end: targetNumber,
    duration: 2500,
    delay: delay,
  });

  // UNIFIED LOGIC: Use the helper for both data types to ensure suffix consistency
  const animatedDisplayValue = formatDisplayValue(mainValue, count);

  return (
    <div
      ref={countRef}
      className={`relative z-40 ${COMPONENT_STYLES.CARD.GLASS} flex min-h-[120px] flex-col justify-center rounded-xl p-4 text-center shadow-lg transition-transform duration-300 hover:scale-105 sm:-mt-20 sm:min-h-[140px] sm:p-6 md:-mt-24`}
    >
      <div className="mx-auto mb-2 flex h-10 w-full items-center justify-center overflow-hidden sm:h-12">
        {mainValue && (
          <div className="text-theme-primary text-2xl leading-tight font-bold sm:text-3xl">
            {/* Template literal ensures the number and suffix stay in a single text node */}
            <span className="tabular-nums">{`${animatedDisplayValue}`}</span>
          </div>
        )}
      </div>

      <div className="text-theme-primary-dark font-mulish mx-auto h-4 max-w-[120px] overflow-hidden text-xs font-medium tracking-wide uppercase sm:h-5 sm:max-w-[157px] sm:text-sm">
        {label &&
          (typeof label === 'string' ? (
            <span className="leading-4 sm:leading-5">{label}</span>
          ) : (
            <RichText
              styles={{
                p: 'text-xs sm:text-sm font-medium uppercase tracking-wide font-mulish *:text-text-body-dark! *:text-xs! sm:*:text-sm!',
              }}
            >
              {label}
            </RichText>
          ))}
      </div>
    </div>
  );
});

AnimatedStatisticCard.displayName = 'AnimatedStatisticCard';

/**
 * StatisticsMetricsWithAnimation - Main orchestrator for the animated stats section.
 */
const StatisticsMetricsWithAnimation: React.FC<StatisticsMetricsProps> = memo(
  ({ data }) => {
    const { ref, sectionClasses, childClasses, getChildStyle } =
      useScrollAnimation();

    const statistics = data?.componentsCollection?.items;
    if (!statistics?.length) return null;

    return (
      <section
        ref={ref}
        className={`relative z-30 py-6 sm:py-8 md:py-12 ${sectionClasses}`}
        data-testid="statistics-metrics"
      >
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          {data.heading?.raw && (
            <div
              className={`mb-6 text-center sm:mb-8 ${childClasses()}`}
              style={getChildStyle(200)}
            >
              <RichText
                styles={{
                  p: 'text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800',
                }}
              >
                {data.heading.raw}
              </RichText>
            </div>
          )}

          <div
            className={`xs:grid-cols-2 mx-auto grid max-w-6xl grid-cols-1 gap-3 sm:gap-4 md:grid-cols-4 md:gap-6 lg:gap-8 ${childClasses()}`}
            style={getChildStyle(400)}
          >
            {statistics.map((item, index) => (
              <div
                key={item.sys.id}
                className={childClasses()}
                style={getChildStyle(600 + index * 150)}
              >
                <AnimatedStatisticCard item={item} delay={index * 200} />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  },
);

StatisticsMetricsWithAnimation.displayName = 'StatisticsMetricsWithAnimation';

export default StatisticsMetricsWithAnimation;
